import React, { useEffect } from "react";
import { matchPath, Redirect, Route, Switch, useLocation } from "react-router-dom";

import {
  DealDataroom,
  DealRouteRoleGuard,
  DefaultViewGuard,
  Notes,
  RoleComponentGuard,
  SurveyBlank,
  TaskManage,
  Template
  // WorkspaceMetadata
} from "components";
import { AmortizationPage } from "components/Deal-amortization/AmortizationPage";
// import { Document } from "components/Documents";
import { MonitoringInvestor } from "components/guards";
import { DealMonitoring } from "components/Measure-data";
// import { MeetingsMetadata } from "components/Meetings-metadata/MeetingsMetadata";
import { DealRedirect } from "constants/deal";
import { Routes } from "constants/routes";
import { useIsReportDeal } from "hooks";
import { DealDashboard, DealReport, DealWorkspace, DebtReport, LookThrough } from "pages";
import { ReportTabs } from "pages/Deal-report/ReportTabs";
import {
  useAccountSelector,
  useDealIdSelector,
  useDealTypeSelect,
  usePickUserPermissions,
  useViewActions
} from "store";
import { IReportType, UserPermissionType } from "store/types";

import { RoleName } from "../../constants";

export const DealRouter: React.FC = (): JSX.Element => {
  const { pathname } = useLocation();
  const dealId = useDealIdSelector();
  const isDebt = useDealTypeSelect() === "debt";
  const isReportDeal = useIsReportDeal();
  const { setDealRedirectAction } = useViewActions();
  const { look_through_investments, show_debt } = useAccountSelector() || {};
  const portfolioAnalysis = usePickUserPermissions("portfolio_analysis");
  const noAccessPermission = portfolioAnalysis && portfolioAnalysis === UserPermissionType.NO_ACCESS;

  const isDealReportRoute = matchPath(pathname, Routes.dealReport) || matchPath(pathname, Routes.dealDebtReport);

  useEffect(() => {
    if (isDealReportRoute && dealId && !isReportDeal) {
      setDealRedirectAction(DealRedirect.Dashboard);
    }
  }, [isReportDeal, isDealReportRoute]);

  return (
    <Switch>
      <Route path={Routes.dealWorkspace} component={DealWorkspace} />

      <Route path={Routes.dealMonitoring}>
        <DealRouteRoleGuard
          availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
          redirectInsideDeal={DealRedirect.Workspace}
        >
          <MonitoringInvestor>
            <DealMonitoring />
          </MonitoringInvestor>
        </DealRouteRoleGuard>
      </Route>

      <Route path={Routes.dealTaskManage}>
        <DealRouteRoleGuard
          availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
          redirectInsideDeal={DealRedirect.Workspace}
        >
          <TaskManage />
        </DealRouteRoleGuard>
      </Route>

      <Route path={Routes.dealDataRoom} exact>
        <DealRouteRoleGuard
          availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
          redirectInsideDeal={DealRedirect.Workspace}
        >
          <DealDataroom />
        </DealRouteRoleGuard>
      </Route>

      <Route exact path={Routes.dealReport}>
        <DealRouteRoleGuard
          availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
          redirectInsideDeal={DealRedirect.Workspace}
          forceRunRedirect={noAccessPermission}
        >
          <DefaultViewGuard ignoreCurrency bookmarkType="report" reportType={IReportType.DEAL_VIEW}>
            <ReportTabs>
              <DealReport />
            </ReportTabs>
          </DefaultViewGuard>
        </DealRouteRoleGuard>
      </Route>

      <Route exact path={Routes.dealDebtReport}>
        <DealRouteRoleGuard
          availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
          redirectInsideDeal={DealRedirect.Workspace}
          forceRunRedirect={noAccessPermission || !show_debt}
        >
          <DefaultViewGuard ignoreCurrency bookmarkType="report" reportType={IReportType.DEAL_VIEW}>
            <ReportTabs>
              <DebtReport />
            </ReportTabs>
          </DefaultViewGuard>
        </DealRouteRoleGuard>
      </Route>

      <Route path={Routes.dealDashboard}>
        <DealRouteRoleGuard
          availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
          redirectInsideDeal={DealRedirect.Workspace}
        >
          <DealDashboard />
        </DealRouteRoleGuard>
      </Route>

      <Route path={Routes.dealLookThrough}>
        <DealRouteRoleGuard
          availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
          redirectInsideDeal={DealRedirect.Workspace}
          forceRunRedirect={!look_through_investments}
        >
          <LookThrough />
        </DealRouteRoleGuard>
      </Route>
      <Route path={Routes.dealAmortization}>
        <DealRouteRoleGuard
          availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
          redirectInsideDeal={DealRedirect.Workspace}
          forceRunRedirect={!show_debt || !isDebt}
        >
          <AmortizationPage />
        </DealRouteRoleGuard>
      </Route>

      <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
        <Route path={Routes.dealNotes} component={Notes} />
      </RoleComponentGuard>
      <Route exact path={Routes.dealFormBlank} component={SurveyBlank} />
      <Route exact path={Routes.deal} />
      <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
        <Route path={Routes.dealTemplate} component={Template} />
      </RoleComponentGuard>
      <Redirect to={Routes.notFound} />
    </Switch>
  );
};
